import React, { useState, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Button } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { deleteStudentRequest, updateStudentRequest, fetchCoursesRequest } from "store/actions";
import CreateStudent from "./CreateStudent";
import ConfirmationModal from "components/Modals/ConfirmationModal";
import StudentDataTable from "components/TableContainers/StudentDataTable";
import { MdDeleteOutline } from "react-icons/md";
import { FaRegEdit, FaRegEye } from "react-icons/fa";

const StudentTable = ({ students, loading, totalrows }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(false);

  const [selectedSortData, setSelectedSortData] = useState({
    value: "createdAt",
    direction: "desc",
  });
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchString, setSearchString] = useState("");

  const [courses, setCourses] = useState([]); //  Added for course filter dropdown

  //  Fetch courses once for filter dropdown
  useEffect(() => {
    const getCourses = async () => {
      try {
        const res = await dispatch(fetchCoursesRequest());
        if (res?.data?.data) {
          setCourses(res.data.data);
        }
        console.log("res:",res)
      } catch (err) {
        console.error("Error fetching courses:", err);
      }
    };
    getCourses();
  }, [dispatch]);

  const permissions = JSON.parse(localStorage.getItem("permissions"));
  const hasEditPermission = permissions?.some(
    (item) => item?.permission_name === "Edit Student"
  );
  const hasDeletePermission = permissions?.some(
    (item) => item?.permission_name === "Delete Student"
  );
  const hasViewPermission = permissions?.some(
    (item) => item?.permission_name === "View Student"
  );

  // Handle deletion confirmation
  const handleDeleteConfirmed = (id) => {
    dispatch(deleteStudentRequest(id));
    setOpenModal(false);
    setConfirmAction(false);
  };



  //  Define Table Columns
const columns = useMemo(
  () => [
    {
      header: "Name",
      accessorKey: "name",
      cell: ({ row }) => (
        <div
          style={{
            maxWidth: "200px",
            whiteSpace: "normal",
            overflow: "hidden",
            textOverflow: "ellipsis",
            wordWrap: "break-word",
            lineHeight: "1.4",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            color: "#222",
          }}
          title={row.original.name}
        >
          {row.original.name || "-"}
        </div>
      ),
    },
    {
      header: "Email",
      accessorKey: "email",
      cell: ({ row }) => (
        <div
          style={{
            maxWidth: "250px",
            whiteSpace: "normal",
            overflow: "hidden",
            textOverflow: "ellipsis",
            wordWrap: "break-word",
            lineHeight: "1.4",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            color: "#444",
          }}
          title={row.original.email}
        >
          {row.original.email || "-"}
        </div>
      ),
    },
    {
      header: "Phone",
      accessorKey: "phone",
      showFilter:false,
      enableSorting: false
      
    },
    {
      header: "Course",
      cell: ({ row }) => (
        <div
          style={{
            maxWidth: "220px",
            whiteSpace: "normal",
            overflow: "hidden",
            textOverflow: "ellipsis",
            wordWrap: "break-word",
            lineHeight: "1.4",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            color: "#555",
          }}
          title={row.original?.course?.title}
        >
          {row.original?.course?.title || "-"}
        </div>
      ),
    },
    {
      header: "Actions",
      id: "actions",
      cell: ({ row }) => {
        const handleEdit = () => {
          setEditData(row.original);
          setIsOpen(true);
        };

        const handleDelete = () => {
          setDeleteId(row.original.id);
          setOpenModal(true);
        };

        const handleView = () => {
          const studentId =  row.original.id;
          navigate(`/view-profile/${studentId}`);
        };

        return (
          <div className="d-flex gap-2">
            {hasViewPermission && (
              <Button color="info" title="View Profile" onClick={handleView}>
                <FaRegEye size={18} />
              </Button>
            )}
            {hasEditPermission && (
              <Button color="primary" title="Edit" onClick={handleEdit}>
                <FaRegEdit size={18} />
              </Button>
            )}
            {hasDeletePermission && (
              <Button color="danger" title="Delete" onClick={handleDelete}>
                <MdDeleteOutline size={18} />
              </Button>
            )}
          </div>
        );
      },
    },
  ],
  [hasEditPermission, hasDeletePermission, hasViewPermission]
);


  const handleFormSubmit = (id, data, onClose) => {
    dispatch(updateStudentRequest(id, data, onClose));
  };

  return (
    <>
      {/*  Confirmation Modal */}
<ConfirmationModal
  okText="Confirm"
  onCancel={() => setOpenModal(false)}
  onOk={() => {
    if (deleteId) handleDeleteConfirmed(deleteId);
  }}
  isVisible={openModal}
  title="Delete Student"
  content="Are you sure you want to delete this student?"
/>


      {/*  Edit/Create Modal */}
      <CreateStudent
        visible={isOpen}
        initialData={editData}
        onSubmit={handleFormSubmit}
        handleClose={() => {
          setIsOpen(false);
          setEditData(null);
        }}
        courses={courses}
      />

      {/*  Table Section */}
      <div className="container-fluid">
        <StudentDataTable
          columns={columns}
          loading={loading}
          data={students || []}
          isGlobalFilter
          isPagination
          selectedSortData={selectedSortData}
          setSelectedSortData={setSelectedSortData}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
          pageSize={pageSize}
          setSearchString={setSearchString}
          searchString={searchString}
          SearchPlaceholder="Search students..."
          pagination="pagination"
          docName="Students"
          totalrows={totalrows}
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />
      </div>
    </>
  );
};

export default StudentTable;
